import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { usePlayground } from '../store'
import { clearLocalData } from '../api/clientId'
import { examples } from '../examples'
import { MobileSheet } from './MobileSheet'
import { Icons } from './TabToolbar'

/**
 * Header action button — icon + label on desktop, icon-only on mobile.
 * Shared styling for Examples / Generate / Clear.
 */
function HeaderAction({ onClick, icon, label, title, danger }: {
    onClick: () => void
    icon: ReactNode
    label: string
    title: string
    danger?: boolean
}) {
    const isMobile = usePlayground((s) => s.isMobile)
    return (
        <button
            onClick={onClick}
            title={title}
            aria-label={label}
            className={`shrink-0 flex items-center gap-1.5 rounded-md transition-colors ${danger ? 'hover:text-danger' : 'hover:text-text'} hover:bg-surface-elevated`}
            style={{
                color: 'var(--text-muted)',
                padding: isMobile ? '6px' : '6px 10px',
                lineHeight: 0,
            }}
        >
            <span className="flex items-center justify-center" style={{ width: 16, height: 16 }}>{icon}</span>
            {!isMobile && <span className="text-xs font-medium font-mono leading-none">{label}</span>}
        </button>
    )
}

const trashIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
)

const aiPromptText = `Write EN notation for my system. I'll describe it below.

EN is a plain-text notation where each statement describes one action:

subject
do: action
needs: input1, input2
yields: output1, output2
at: context

Multiple actors can share the same action — list them comma-separated
before \`do:\`. One action node, multiple performers:

actor1, actor2
do: sameAction
needs: something
yields: somethingElse
at: context

Example — an e-commerce checkout:

cart service
do: calculate totals
needs: cart items, pricing rules
yields: order summary, tax amount
at: frontend

payment gateway
do: process payment
needs: order summary, payment method
yields: payment confirmation, receipt
at: payments

# Two actors collaborate on the same sign-off action
finance, legal
do: approve refund
needs: refund request
yields: refund approved
at: ops

fulfillment
do: ship order
needs: payment confirmation, shipping address
yields: tracking number
at: warehouse

Rules:
- Each statement must have subject, do, needs, and yields
- at: is optional but recommended — it groups actions by context (service, team, domain, layer)
- Comma-separated subjects share ONE action node (actor1, actor2 do: sameAction)
- Shared entity names create connections automatically
- Use plain English names
- Aim for 4-12 statements
- Every needs and yields must have at least one item

Output ONLY the EN code, no explanations. Here is my system:

[DESCRIBE YOUR SYSTEM HERE]`

export function AppHeader() {
    const projectName = usePlayground((s) => s.projectName)
    const setProjectName = usePlayground((s) => s.setProjectName)
    const isMobile = usePlayground((s) => s.isMobile)
    const loadExample = usePlayground((s) => s.loadExample)

    const [editing, setEditing] = useState(false)
    const [localName, setLocalName] = useState(projectName)
    const [showExamples, setShowExamples] = useState(false)
    const [showAIPrompt, setShowAIPrompt] = useState(false)
    const [promptCopied, setPromptCopied] = useState(false)

    useEffect(() => { setLocalName(projectName) }, [projectName])

    const handleBlur = () => { setEditing(false); setProjectName(localName) }
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleBlur()
        if (e.key === 'Escape') { setEditing(false); setLocalName(projectName) }
    }

    const handleClearLocalData = () => {
        const confirmed = window.confirm(
            'Clear all local data?\n\n' +
            'This will permanently delete:\n' +
            '  • Your editor state and project name\n' +
            '  • Theme preference\n' +
            '  • Anonymous install ID\n\n' +
            'You will lose any unsaved work in this browser. ' +
            'This cannot be undone.'
        )
        if (confirmed) {
            clearLocalData()
            window.location.reload()
        }
    }

    const handleCopyPrompt = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(aiPromptText)
            setPromptCopied(true)
            setTimeout(() => setPromptCopied(false), 2000)
        } catch {}
    }, [])

    return (
        <header
            className="flex items-center gap-3 px-4 shrink-0 z-50 h-11"
            style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)' }}
        >
            <a href="https://endiagram.com" className="text-sm font-black tracking-tight text-accent font-mono uppercase shrink-0">
                endiagram
            </a>

            <div className="w-px h-4 shrink-0" style={{ background: 'var(--border)' }} />

            {editing ? (
                <input
                    autoFocus
                    className="text-sm text-text outline-none px-1.5 py-0.5 rounded-xl border font-mono bg-transparent shrink min-w-0"
                    style={{ borderColor: 'color-mix(in srgb, var(--accent) 40%, transparent)', minWidth: 40, maxWidth: isMobile ? 140 : 200 }}
                    value={localName}
                    onChange={e => setLocalName(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder="project name"
                />
            ) : (
                <button
                    className="text-sm font-medium transition-colors cursor-text truncate font-mono hover:text-accent shrink min-w-0"
                    style={{ color: projectName ? 'var(--text-secondary)' : 'var(--text-muted)', maxWidth: isMobile ? 140 : 200 }}
                    onClick={() => setEditing(true)}
                >
                    {projectName || 'untitled'}
                </button>
            )}

            {/* Flexible spacer pushes actions to the far right — project title is
                capped by its own maxWidth and shrinks if the viewport is narrow,
                so actions can never collide with it. */}
            <div className="flex-1 min-w-2" />

            <div className="flex items-center gap-1 shrink-0">
                <button
                    onClick={() => setShowExamples(true)}
                    title="Browse example diagrams or generate with AI"
                    aria-label="Examples"
                    className="shrink-0 flex items-center gap-1.5 rounded-md transition-colors hover:text-text hover:bg-surface-elevated"
                    style={{ color: 'var(--text-muted)', padding: '6px 10px', lineHeight: 0 }}
                >
                    <span className="flex items-center justify-center" style={{ width: 16, height: 16 }}>{Icons.examples}</span>
                    <span className="text-xs font-medium font-mono leading-none">Examples</span>
                </button>
                <HeaderAction onClick={handleClearLocalData} icon={trashIcon} label="Clear" title="Clear all local data — editor state, theme, install ID. Asks for confirmation first." danger />
            </div>

            {/* AI Generate sheet */}
            <MobileSheet open={showAIPrompt} onClose={() => setShowAIPrompt(false)} icon={Icons.generate} title="Generate with AI">
                <div className="space-y-2 mb-4">
                    {['Copy the prompt below', 'Paste in ChatGPT, Claude, or any AI', 'Replace [DESCRIBE YOUR SYSTEM HERE] with your system', 'Paste the AI output back here'].map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                            <span className="text-xs font-bold font-mono text-accent shrink-0 mt-0.5">{i + 1}</span>
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{step}</span>
                        </div>
                    ))}
                </div>
                <button onClick={handleCopyPrompt} className="w-full py-2.5 rounded-xl text-xs font-bold font-mono tracking-wider transition-colors active:scale-[0.97]" style={{ background: promptCopied ? 'color-mix(in srgb, var(--success) 20%, transparent)' : 'color-mix(in srgb, var(--accent) 20%, transparent)', color: promptCopied ? 'var(--success)' : 'var(--accent)' }}>
                    {promptCopied ? 'Copied to clipboard!' : 'Copy prompt'}
                </button>
            </MobileSheet>

            {/* Examples sheet — also hosts the AI Generate trigger */}
            <MobileSheet open={showExamples} onClose={() => setShowExamples(false)} icon={Icons.examples} title="Examples">
                <button
                    onClick={() => { setShowExamples(false); setShowAIPrompt(true) }}
                    className="w-full flex items-center gap-2 px-3 py-3 transition-colors border-b active:scale-[0.97]"
                    style={{ borderColor: 'var(--border-subtle)', background: 'color-mix(in srgb, var(--accent) 8%, transparent)' }}
                    title="Generate EN notation from a prompt via your favourite AI"
                >
                    <span className="flex items-center justify-center" style={{ width: 16, height: 16, color: 'var(--accent)' }}>{Icons.generate}</span>
                    <span className="flex-1 text-left">
                        <span className="block text-xs font-bold font-mono uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Generate with AI</span>
                        <span className="block text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Build your own from a plain-English description</span>
                    </span>
                </button>
                {examples.map((ex, i) => (
                    <button key={i} className="w-full text-left px-3 py-3 transition-colors border-b last:border-0 active:scale-[0.97]" style={{ borderColor: 'var(--border-subtle)' }}
                        onClick={() => { loadExample(ex.source, ex.name); setShowExamples(false) }}>
                        <div className="text-xs font-medium" style={{ color: 'var(--text)' }}>{ex.name}</div>
                        <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{ex.description}</div>
                    </button>
                ))}
            </MobileSheet>
        </header>
    )
}
